import { env } from '@/config/env'
import { authStore } from '@/auth/authStore'
import { getIdToken } from '@/auth/firebase'
import { buildLoginPath, LOGIN_PATH } from '@/auth/redirect'
import type { ApiErrorBody, ApiFieldError } from '@/types/admin'

/**
 * The only place in the app that calls `fetch`. Owns: base URL, JSON encoding,
 * the error envelope, bearer tokens, and the single-flight refresh dance.
 */

export type QueryValue = string | number | boolean | null | undefined | (string | number)[]
export type QueryParams = Record<string, QueryValue>

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type RequestOptions = {
  method?: HttpMethod
  /** Plain object -> JSON. FormData -> multipart, untouched. */
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  headers?: Record<string, string>
  /** Send the bearer token and refresh on 401. Default true. */
  auth?: boolean
  /**
   * Whether a terminal auth failure should bounce the user to /login.
   * Session bootstrap passes false: it is *asking* whether a session is valid, and
   * a "no" is an answer, not an interruption.
   */
  redirectOnAuthFailure?: boolean
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Every non-2xx response, and every transport failure, arrives as one of these. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly errors: ApiFieldError[]
  readonly traceId: string | undefined

  constructor(init: {
    code: string
    message: string
    status: number
    errors?: ApiFieldError[]
    traceId?: string
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.errors = init.errors ?? []
    this.traceId = init.traceId
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED' || this.status === 400
  }

  get isNotFound(): boolean {
    return this.code === 'NOT_FOUND' || this.status === 404
  }

  get isConflict(): boolean {
    return this.code === 'CONFLICT' || this.status === 409
  }

  get isForbidden(): boolean {
    return this.code === 'FORBIDDEN' || this.status === 403
  }

  get isUnauthorised(): boolean {
    return this.code === 'UNAUTHORISED' || this.status === 401
  }

  get isNetwork(): boolean {
    return this.code === 'NETWORK_ERROR'
  }

  /** `{ fieldName: message }` for wiring straight into react-hook-form. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {}
    for (const error of this.errors) {
      // Backend field paths may be PascalCase (`BasePrice`) — forms use camelCase.
      const key = error.field.charAt(0).toLowerCase() + error.field.slice(1)
      if (!(key in result)) result[key] = error.message
    }
    return result
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

// ---------------------------------------------------------------------------
// URL building
// ---------------------------------------------------------------------------

function buildUrl(path: string, query?: QueryParams): string {
  const base = `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return base

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }

  const queryString = params.toString()
  return queryString ? `${base}?${queryString}` : base
}

// ---------------------------------------------------------------------------
// Terminal auth failure
// ---------------------------------------------------------------------------

type UnauthorisedHandler = () => void

let unauthorisedHandler: UnauthorisedHandler | null = null

/**
 * Lets the router perform an in-app navigation instead of a full page load.
 * Wired once in src/router.tsx.
 */
export function setUnauthorisedHandler(handler: UnauthorisedHandler | null): void {
  unauthorisedHandler = handler
}

function handleTerminalAuthFailure(): void {
  authStore.clear()

  if (unauthorisedHandler) {
    unauthorisedHandler()
    return
  }

  // Fallback for code running before the router is wired.
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
    window.location.assign(buildLoginPath(window.location))
  }
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

function buildRequestInit(options: RequestOptions, idToken: string | null): RequestInit {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  let body: BodyInit | undefined
  if (options.body instanceof FormData) {
    // NEVER set Content-Type for FormData: only the browser can append the
    // multipart boundary, and setting it by hand makes the upload unparseable.
    body = options.body
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }

  if (idToken) headers.set('Authorization', `Bearer ${idToken}`)

  return {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
    // Tokens travel in the Authorization header; no cookies are involved.
    credentials: 'omit',
  }
}

function networkError(cause: unknown): ApiError {
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    // Preserve abort semantics so TanStack Query can ignore it.
    throw cause
  }
  return new ApiError({
    code: 'NETWORK_ERROR',
    status: 0,
    message: 'Could not reach the server. Check your connection and try again.',
    errors: [],
  })
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {}
  try {
    const text = await response.text()
    if (text) body = JSON.parse(text) as Partial<ApiErrorBody>
  } catch {
    // Non-JSON error page (proxy timeout, HTML 502): fall through to the defaults.
  }

  return new ApiError({
    code: body.code ?? fallbackCode(response.status),
    status: response.status,
    message: body.message ?? fallbackMessage(response.status),
    errors: body.errors ?? [],
    traceId: body.traceId,
  })
}

function fallbackCode(status: number): string {
  if (status === 401) return 'UNAUTHORISED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 429) return 'RATE_LIMITED'
  if (status >= 500) return 'INTERNAL_ERROR'
  return 'VALIDATION_FAILED'
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'That record no longer exists.'
  if (status === 409) return 'That change conflicts with the current data.'
  if (status === 429) return 'Too many attempts. Wait a moment and try again.'
  if (status >= 500) return 'Something went wrong on the server. Please try again.'
  return 'The request could not be completed.'
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('json')) {
    const text = await response.text()
    return (text ? text : undefined) as T
  }

  const text = await response.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const requiresAuth = options.auth ?? true
  const redirectOnAuthFailure = options.redirectOnAuthFailure ?? true

  // Read the token per request rather than caching it. The Firebase SDK renews in the
  // background and hands back whatever is current, so this is a memory read in the
  // common case and a refresh only when one is genuinely due.
  let response: Response
  try {
    response = await fetch(
      buildUrl(path, options.query),
      buildRequestInit(options, requiresAuth ? await getIdToken() : null),
    )
  } catch (cause) {
    throw networkError(cause)
  }

  if (response.status === 401 && requiresAuth) {
    // One retry with a force-refreshed token. This covers the narrow window where the
    // cached token expired between being read and reaching the API — clock skew, a
    // suspended laptop, a slow request. Exactly one retry: if a fresh token is also
    // rejected, the account has lost access and retrying cannot change that.
    //
    // Far simpler than what this replaced. Rotating refresh tokens needed single-flight
    // coordination because two concurrent refreshes looked like replay and revoked the
    // whole session; Firebase ID tokens do not rotate on use, so parallel requests
    // asking for one is harmless.
    const freshToken = await getIdToken(true)

    if (freshToken) {
      try {
        response = await fetch(buildUrl(path, options.query), buildRequestInit(options, freshToken))
      } catch (cause) {
        throw networkError(cause)
      }
    }

    if (!freshToken || response.status === 401) {
      // Either Firebase has no session, or it has one the API refuses — an account with
      // no staff row, or one that has been deactivated. Both mean signed out here.
      if (redirectOnAuthFailure) handleTerminalAuthFailure()
      else authStore.clear()
      throw await toApiError(response)
    }
  }

  if (!response.ok) throw await toApiError(response)
  return parseBody<T>(response)
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
}
